import { Fragment, useEffect, useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Level, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardButton, BoardFrame, INK, PALETTE, RADIUS, STROKE, TYPE, alpha, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';

/**
 * SIGNAL OFFICER · COPY THE SIGNAL — watch the dishes light up, then tap them back in the same order.
 *
 * Four dishes on the roof of the radio room, each its own colour (from the kit's six) and its own
 * lamp shape, so neither colour nor place alone has to carry the memory. A signal plays across
 * them while the strip along the top counts its lights; then the guest sends it back, a dish at a
 * time. A wrong dish is the mistake, and the same signal plays again from the start; covered by an
 * alarm mid-signal, it plays again from the start too. Round 1 is three lights; round 2 three, a
 * little faster; round 3 four, a fifth faster. No dish lights twice in a row, so every light in a
 * signal is plainly its own.
 *
 * A converted board (kit.tsx): the rules as plain functions, a small view state (`EchoView`), a
 * pure `View` with the dishes as the kit's buttons, the `Board` that plays it, and the `demo`.
 */

export const PADS = 4;

/** Each round's signal: how many lights, and how fast it plays (× the timings below). */
/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const ECHO: readonly { lights: number; speed: number }[] = easier([
  { lights: 3, speed: 1 },
  { lights: 4, speed: 0.8 },
]);

/** Milliseconds: the pause before a signal, a light on, the dark between lights (× speed), and the wait after a wrong dish. */
export const ECHO_MS = { lead: 700, on: 620, gap: 280, retry: 1_000 } as const;

/** The four dishes, from the kit's six colours: the same in every era, since they are the thing remembered. */
const PAD_COLOURS = [PALETTE[2], PALETTE[0], PALETTE[4], PALETTE[3]] as const;

/** A signal for a round: its lights, never the same dish twice in a row. */
export function echoSignal(level: Level, rand: () => number): number[] {
  const { lights } = ECHO[level]!;
  const out: number[] = [];
  while (out.length < lights) {
    const prev = out[out.length - 1];
    let pad = Math.floor(rand() * PADS) % PADS;
    if (pad === prev) pad = (pad + 1 + (Math.floor(rand() * (PADS - 1)) % (PADS - 1))) % PADS;
    out.push(pad);
  }
  return out;
}

/** How long a signal of `lights` takes to play, lead included. */
export function echoPlayMs(lights: number, speed: number): number {
  return ECHO_MS.lead + lights * (ECHO_MS.on + ECHO_MS.gap) * speed - ECHO_MS.gap * speed;
}

/** Which light of the signal is on `t` ms into its playback, or -1 between lights. */
export function echoStepAt(lights: number, speed: number, t: number): number {
  if (t < ECHO_MS.lead) return -1;
  const per = (ECHO_MS.on + ECHO_MS.gap) * speed;
  const step = Math.floor((t - ECHO_MS.lead) / per);
  if (step >= lights) return -1;
  return t - ECHO_MS.lead - step * per < ECHO_MS.on * speed ? step : -1;
}

/** A tap's own light: short, so a quick second tap is still seen. */
const TOUCH_MS = 240;

/** Everything the roof shows. */
export interface EchoView {
  /** The game clock, ms. */
  t: number;
  /** The signal: the dishes in the order they light. */
  sig: readonly number[];
  /** 0 the signal plays, 1 the guest sends it back, 2 a wrong dish (it plays again), 3 sent. */
  s: 0 | 1 | 2 | 3;
  /** Lights sent back so far. */
  n: number;
  /** The light of the signal playing now (its place in `sig`), or −1. */
  st: number;
  /** A dish lit, by the signal or by a right tap: which, and how long it has been lit (ms). */
  lit: readonly [pad: number, age: number] | null;
  /** The dish under the finger now (shown pressed), or −1. */
  p: number;
  /** The wrong dish, while it shows, or −1. */
  w: number;
  fx?: Fx | undefined;
}

const W = BOARD.w;
const H = BOARD.h;
/** The signal strip along the top, and the four dishes under it, two by two. */
const STRIP = { x: 10, y: 8, w: W - 20, h: 40 } as const;
const GAP = 10;
const PAD_W = (W - 20 - GAP) / 2;
const PAD_TOP = STRIP.y + STRIP.h + GAP;
const PAD_H = (H - 10 - PAD_TOP - GAP) / 2;
/** A dish's button: its size, and its top left. */
export const PAD = { w: PAD_W, h: PAD_H } as const;
export const padAt = (i: number) => ({ x: 10 + (i % 2) * (PAD_W + GAP), y: PAD_TOP + Math.floor(i / 2) * (PAD_H + GAP) });
/** The middle of a dish, where a finger taps it. */
const padMid = (i: number) => ({ x: Math.round(padAt(i).x + PAD_W / 2), y: Math.round(padAt(i).y + PAD_H / 2) });

/** The lamp at a dish's focus: a different shape on each, for guests who don't tell the colours apart. */
function Lamp({ index, fill, stroke }: { index: number; fill: string; stroke: string }) {
  const common = { fill, stroke, strokeWidth: 1.4, strokeLinejoin: 'round' as const };
  if (index === 0) return <circle r="6.5" {...common} />;
  if (index === 1) return <path d="M0 -7.5 L7 5 L-7 5 Z" {...common} />;
  if (index === 2) return <rect x="-5.8" y="-5.8" width="11.6" height="11.6" rx="1.5" {...common} />;
  return <path d="M0 -8 L7.2 0 L0 8 L-7.2 0 Z" {...common} />;
}

/**
 * One dish on the roof, centred on (0, 0) inside its button: its mast, the bowl, the lamp at its
 * focus, and the rings it sends out while it is lit. It sways a little in the wind, from the clock.
 */
function Dish({ index, lit, age, t, haze, ink, id }: { index: number; lit: boolean; age: number; t: number; haze: string; ink: string; id: string }) {
  const colour = PAD_COLOURS[index]!;
  // The two dishes on the left lean left, the two on the right lean right: four dishes, four places.
  const tilt = index % 2 === 0 ? -13 : 13;
  const period = 6 + index;
  const sway = -2 + 5 * (0.5 - 0.5 * Math.cos((2 * Math.PI * (t / 1000 - index * 0.8)) / period));
  // The rings travel outward while it speaks; they don't blink.
  const rings = lit
    ? [0, 1, 2].map((k) => {
        const travel = ((age / 1000) * 62 + k * 15) % 44;
        return { r: 10 + travel, o: Math.max(0, 1 - travel / 44) };
      })
    : [];
  return (
    <g transform="scale(1.06) translate(-70 -57)">
      {/* the roof and the plinth */}
      <path d="M18 106 L122 106" stroke={ink} strokeOpacity=".35" strokeWidth="1.2" />
      <path d="M52 106 L88 106 L82 96 L58 96 Z" fill={alpha(ink, 0.19)} stroke={ink} strokeOpacity=".55" strokeWidth="1" />
      <rect x="66.5" y="72" width="7" height="25" rx="2" fill={`url(#${id}m)`} />
      <g transform={`rotate(${(tilt + sway).toFixed(2)} 70 76)`}>
        {rings.map((ring, k) => (
          <path
            key={k}
            d={`M${70 - ring.r * 0.77} ${34 - ring.r * 0.64} A ${ring.r} ${ring.r} 0 0 1 ${70 + ring.r * 0.77} ${34 - ring.r * 0.64}`}
            fill="none"
            stroke={colour}
            strokeWidth="2.2"
            strokeLinecap="round"
            opacity={ring.o * 0.85}
          />
        ))}
        {/* the bowl, seen from the side, and its face */}
        <path d="M32 56 Q70 98 108 56 Z" fill={`url(#${id}b${index})`} stroke={colour} strokeOpacity={lit ? 1 : 0.6} strokeWidth="1.6" strokeLinejoin="round" />
        <ellipse cx="70" cy="56" rx="38" ry="8.5" fill={`url(#${id}f${index})`} stroke={colour} strokeOpacity={lit ? 1 : 0.7} strokeWidth="1.6" />
        <path d="M40 60 Q70 70 100 60" fill="none" stroke={INK.hi} strokeOpacity=".12" strokeWidth="1" />
        {/* the arms up to the lamp */}
        <path d="M44 57 L68 38 M96 57 L72 38 M70 64 L70 42" stroke={haze} strokeOpacity=".5" strokeWidth="1.3" strokeLinecap="round" />
        {lit && <circle cx="70" cy="34" r="20" fill={`url(#${id}h${index})`} />}
        <g transform="translate(70 34)">
          <Lamp index={index} fill={lit ? INK.hi : alpha(colour, 0.33)} stroke={colour} />
        </g>
      </g>
    </g>
  );
}

/** The roof, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<EchoView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  // A demo glides every number between its keys: states and counts are read rounded.
  const state = Math.round(view.s);
  const sent = Math.round(view.n);
  const step = Math.round(view.st);
  const pressed = Math.round(view.p);
  const wrong = Math.round(view.w);
  const lit = view.lit ? { pad: Math.round(view.lit[0]), age: view.lit[1] } : null;
  const of = view.sig.length;
  const status =
    state === 3
      ? { text: say(lang, '✓ SIGNAL SENT', '✓ SIGNALET SENDT'), tone: 'ok' as const }
      : state === 2
        ? { text: say(lang, 'WRONG DISH — WATCH AGAIN', 'FEIL – SE IGJEN'), tone: 'bad' as const }
        : state === 1
          ? { text: say(lang, 'YOUR TURN', 'DIN TUR'), count: [sent, of] as const, hint: say(lang, 'SAME ORDER', 'SAMME REKKEFØLGE') }
          : { text: say(lang, 'WATCH', 'SE NØYE'), hint: say(lang, 'REMEMBER THE ORDER', 'HUSK REKKEFØLGEN') };
  const press = input?.press;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}m`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={INK.onFill} />
          <stop offset=".45" stopColor={INK.faint} />
          <stop offset="1" stopColor={INK.onFill} />
        </linearGradient>
        {PAD_COLOURS.map((colour, i) => {
          const on = lit?.pad === i;
          return (
            <Fragment key={i}>
              <linearGradient id={`${id}b${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={colour} stopOpacity={on ? 0.75 : 0.28} />
                <stop offset="1" stopColor={INK.onFill} stopOpacity=".9" />
              </linearGradient>
              <radialGradient id={`${id}f${i}`} cx=".5" cy=".5" r=".6">
                <stop offset="0" stopColor={colour} stopOpacity={on ? 0.85 : 0.18} />
                <stop offset="1" stopColor={colour} stopOpacity={on ? 0.25 : 0.04} />
              </radialGradient>
              <radialGradient id={`${id}h${i}`} cx=".5" cy=".5" r=".5">
                <stop offset="0" stopColor={colour} stopOpacity=".7" />
                <stop offset="1" stopColor={colour} stopOpacity="0" />
              </radialGradient>
            </Fragment>
          );
        })}
      </defs>

      {/* the transmission, across the top: a lamp for each light of the signal, filled as it is sent back */}
      <rect x={STRIP.x} y={STRIP.y} width={STRIP.w} height={STRIP.h} rx={RADIUS.button} fill={alpha(INK.onFill, 0.55)} stroke={alpha(skin.ink, 0.2)} strokeWidth={STROKE.hair} />
      <text x={STRIP.x + 12} y={STRIP.y + STRIP.h / 2 + TYPE.hint * 0.36} fontFamily={mono} fontSize={TYPE.hint} fontWeight="700" letterSpacing="1.6" fill={skin.haze} opacity=".7">
        {state === 0 ? say(lang, 'INCOMING', 'MOTTAR') : say(lang, 'SEND BACK', 'SEND TILBAKE')}
      </text>
      {view.sig.map((pad, i) => {
        const done = state === 3 || (state === 1 && i < sent);
        const next = state === 1 && i === sent;
        const now = state === 0 && i === step;
        const colour = PAD_COLOURS[Math.round(pad)]!;
        const edge = state === 2 ? BAD : done ? colour : next ? accent : now ? INK.hi : alpha(INK.hi, 0.22);
        return (
          <circle
            key={i}
            cx={STRIP.x + STRIP.w - 22 - (of - 1 - i) * 28}
            cy={STRIP.y + STRIP.h / 2}
            r={now || next ? 10 : 9}
            fill={done ? colour : alpha(INK.onFill, 0.35)}
            stroke={edge}
            strokeWidth={STROKE.line}
          />
        );
      })}

      {/* the four dishes, each a button */}
      {PAD_COLOURS.map((colour, i) => {
        const at = padAt(i);
        const on = lit?.pad === i;
        return (
          <BoardButton
            key={i}
            x={at.x}
            y={at.y}
            w={PAD_W}
            h={PAD_H}
            label={say(lang, `Dish ${i + 1}`, `Antenne ${i + 1}`)}
            accent={accent}
            tone={wrong === i ? 'bad' : 'plain'}
            down={pressed === i}
            onPress={press && (() => press(i))}
          >
            {on && <rect x={-PAD_W / 2} y={-PAD_H / 2} width={PAD_W} height={PAD_H} rx={RADIUS.button} fill={alpha(colour, 0.16)} stroke={colour} strokeWidth={STROKE.line} />}
            <Dish index={i} lit={on} age={lit?.age ?? 0} t={view.t} haze={skin.haze} ink={skin.ink} id={id} />
          </BoardButton>
        );
      })}
    </BoardFrame>
  );
}

type Phase = 'playing' | 'listening' | 'wrong';
const PHASE: Record<Phase, 0 | 1 | 2> = { playing: 0, listening: 1, wrong: 2 };

function Board(props: RoundProps) {
  const { level, lang } = props;
  const spec = ECHO[level]!;
  const [signal] = useState(() => echoSignal(level, seeded(Date.now() + level * 7919)));
  const phase = useRef<Phase>('playing');
  /** When the current phase began, on the game clock (null until the first frame). */
  const since = useRef<number | null>(null);
  const entered = useRef(0);
  const touch = useRef<{ pad: number; at: number; wrong: boolean } | null>(null);

  const go = (next: Phase, now: number) => {
    phase.current = next;
    since.current = now;
  };

  const round = useRound(props, (_dt, now, won) => {
    if (since.current === null) since.current = now;
    if (won) return;
    const t = now - since.current;
    if (phase.current === 'playing' && t >= echoPlayMs(signal.length, spec.speed)) go('listening', now);
    else if (phase.current === 'wrong' && t >= ECHO_MS.retry) go('playing', now);
  });

  // Covered mid-signal, the guest missed part of it: it plays again from the start once the board is back.
  const time = round.time;
  useEffect(() => {
    if (!props.paused && phase.current === 'playing' && since.current !== null) since.current = time();
  }, [props.paused, time]);

  const press = (pad: number) => {
    if (!round.live || phase.current !== 'listening') return;
    const now = round.time();
    if (signal[entered.current] === pad) {
      touch.current = { pad, at: now, wrong: false };
      entered.current += 1;
      round.hit();
      round.progress(entered.current / signal.length);
      if (entered.current >= signal.length) round.win();
      return;
    }
    // The wrong dish: a mistake, and the same signal again from its start.
    touch.current = { pad, at: now, wrong: true };
    entered.current = 0;
    round.miss();
    go('wrong', now);
  };

  const now = round.now;
  const t = since.current === null ? 0 : now - since.current;
  const p = phase.current;
  const step = p === 'playing' && !round.won ? echoStepAt(signal.length, spec.speed, t) : -1;
  const per = (ECHO_MS.on + ECHO_MS.gap) * spec.speed;
  const touched = touch.current && now - touch.current.at < TOUCH_MS ? touch.current : null;
  const view: EchoView = {
    t: q(now),
    sig: signal,
    s: round.won ? 3 : PHASE[p],
    n: entered.current,
    st: step,
    lit: step >= 0 ? [signal[step]!, q(t - ECHO_MS.lead - step * per)] : touched && !touched.wrong ? [touched.pad, q(now - touched.at)] : null,
    p: touched ? touched.pad : -1,
    w: p === 'wrong' && touch.current?.wrong ? touch.current.pad : -1,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap the dishes in the order they lit', 'Trykk på antennene i rekkefølgen de lyste'), press }}
    />
  );
}

/** The how-to's signal: round 1's three lights. */
const DEMO_SIGNAL = [1, 3, 0] as const;
/** When the finger taps each dish back. */
const DEMO_TAPS = [3_900, 4_700, 5_500] as const;

/**
 * The how-to: round 1's signal plays across the dishes — top right, bottom right, top left — and
 * the strip counts it; then the finger taps the same three in the same order, and it is sent.
 */
function demoSignal(): DemoScript<EchoView> {
  const keys: DemoKey<EchoView>[] = [{ at: 0, view: { t: 0, sig: DEMO_SIGNAL, s: 0, n: 0, st: -1, lit: null, p: -1, w: -1 } }];
  DEMO_SIGNAL.forEach((pad, k) => {
    const at = ECHO_MS.lead + k * (ECHO_MS.on + ECHO_MS.gap);
    keys.push(
      { at, view: { st: k, lit: [pad, 0] }, ease: 'step' },
      { at: at + ECHO_MS.on - 1, view: { lit: [pad, ECHO_MS.on - 1] } },
      { at: at + ECHO_MS.on, view: { st: -1, lit: null }, ease: 'step' },
    );
  });
  keys.push({ at: echoPlayMs(DEMO_SIGNAL.length, 1), view: { s: 1 }, ease: 'step' });
  const finger: FingerKey[] = [
    { at: 0, x: 280, y: 300, hide: true },
    { at: 3_000, x: 280, y: 300, hide: false },
  ];
  DEMO_TAPS.forEach((at, k) => {
    const pad = DEMO_SIGNAL[k]!;
    const last = k === DEMO_TAPS.length - 1;
    keys.push(
      { at, view: { n: k + 1, lit: [pad, 0], p: pad, fx: [1, 0], ...(last ? { s: 3 as const } : {}) }, ease: 'step' },
      { at: at + TOUCH_MS, view: { lit: [pad, TOUCH_MS], fx: [0.47, 0] } },
      { at: at + TOUCH_MS + 1, view: { lit: null, p: -1 }, ease: 'step' },
      { at: at + 450, view: { fx: [0, 0] } },
    );
    const mid = padMid(pad);
    finger.push({ at: at - 400, x: mid.x, y: mid.y }, { at, x: mid.x, y: mid.y, down: true }, { at: at + 160, x: mid.x, y: mid.y, down: false });
  });
  finger.push({ at: 6_400, x: 280, y: 300, hide: true });
  return {
    ms: 7_200,
    keys,
    finger,
    steps: [
      { at: 0, en: 'WATCH THE DISHES LIGHT UP', no: 'SE ANTENNENE LYSE OPP' },
      { at: 3_200, en: 'TAP THEM IN THE SAME ORDER', no: 'TRYKK DEM I SAMME REKKEFØLGE' },
    ],
  };
}

export const game = thirtyGame<EchoView>({
  Board,
  View,
  demo: demoSignal(),
  words: {
    en: {
      instruction: 'Watch the dishes light up, then tap them in the same order. Faster, then longer.',
      howMove: 'Watch the lights. Then tap the dishes in the same order.',
    },
    no: {
      instruction: 'Se antennene lyse, og trykk på dem i samme rekkefølge. Raskere, så lengre.',
      howMove: 'Se på lysene. Trykk så på antennene i samme rekkefølge.',
    },
  },
});
